import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useMsal } from '@azure/msal-react';
import { GET_SELF } from '../graphql/queries';
import { User } from '../types';
import { AUTH_EXPIRED_EVENT } from '../graphql/client';

const AUTH_EXPIRED_PHRASES = [
  'missing authentication claims',
  'please re-authenticate',
  'invalid credentials',
  'unauthenticated',
];

const isAuthExpiredMessage = (msg: string) =>
  AUTH_EXPIRED_PHRASES.some(p => msg.toLowerCase().includes(p));

interface UserRoleState {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  isNetworkError: boolean;
}

/**
 * Compute mapping-aware permissions for a user.
 *
 * - Role 1 (Super Admin): purely user roleId — no mapping needed.
 * - Role ≤ 2 / ≤ 98: if the user has mappings, at least one must satisfy the
 *   role threshold. If the user has NO mappings yet (legacy / pre-mapping
 *   accounts), fall back to role-only check so existing users aren't locked out.
 */
export const computePermissions = (user: User | null | undefined) => {
  const mappings = user?.mappings ?? [];
  const hasMappings = mappings.length > 0;
  const isSuperAdmin = user?.roleId === 1;
  const isClientAdmin =
    isSuperAdmin ||
    ((user?.roleId ?? 999) <= 2 && (!hasMappings || mappings.some((m) => m.roleId <= 2)));
  const isPaidUser =
    isClientAdmin ||
    ((user?.roleId ?? 999) <= 98 && (!hasMappings || mappings.some((m) => m.roleId <= 98)));
  return { isSuperAdmin, isClientAdmin, isPaidUser };
};

/**
 * Hook to fetch user information and check control panel role using GetSelf endpoint
 * Control panel access is granted to users with roleId ≤ 2 AND a matching tenant mapping.
 */
export const useUserRole = () => {
  const { instance } = useMsal();
  const [state, setState] = useState<UserRoleState>({
    user: null,
    isAdmin: false,
    isLoading: true,
    error: null,
    isNetworkError: false,
  });

  const triggerLogout = useCallback(() => {
    console.warn('[UserRole] Session expired — logging out');
    localStorage.removeItem('msal.token');
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin }).catch(() => {
      window.location.href = '/';
    });
  }, [instance]);

  // Listen for the global auth-expired event from the Apollo error link
  useEffect(() => {
    window.addEventListener(AUTH_EXPIRED_EVENT, triggerLogout);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, triggerLogout);
  }, [triggerLogout]);

  const { data, loading, error, refetch } = useQuery(GET_SELF, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    if (loading) {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      return;
    }

    if (error) {
      console.error('❌ [UserRole] Failed to fetch user info:', error);
      const msg = error.message || '';
      if (isAuthExpiredMessage(msg)) {
        triggerLogout();
        return;
      }
      const networkFailure = error.networkError != null;
      setState({
        user: null,
        isAdmin: false,
        isLoading: false,
        error: error.message || 'Failed to fetch user information',
        isNetworkError: networkFailure,
      });
      return;
    }

    if (data?.getSelf) {
      const userData: User = data.getSelf;
      
      // Add backward compatibility aliases
      const user: User = {
        ...userData
      };
      
      // Check if user has admin role (roleId ≤ 2 with matching tenant mapping)
      const { isClientAdmin: isAdmin } = computePermissions(user);
      
      console.log('✅ [UserRole] User info loaded from GetSelf:', {
        id: user.id,
        username: user.username,
        emailAddress: user.emailAddress,
        displayName: user.displayName,
        roleId: user.roleId,
        isAdmin,
        hasControlPanelAccess: isAdmin
      });

      setState({
        user,
        isAdmin,
        isLoading: false,
        error: null,
        isNetworkError: false,
      });
    } else {
      console.warn('⚠️ [UserRole] No user data returned from GetSelf query');
      setState({
        user: null,
        isAdmin: false,
        isLoading: false,
        error: 'No user information available from GetSelf endpoint',
        isNetworkError: false,
      });
    }
  }, [data, loading, error, triggerLogout]);

  return {
    ...state,
    refetch
  };
};
